import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Download } from "@/lib/icons";
import { normalizeScreeningQuestions, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useEveryApplication } from "./-components/use-every-application";
import { ALL, TrackerFilters, type TrackerFilterState } from "./-components/tracker-filters";
import { TrackerTable, trackerColumns, type TrackerRow } from "./-components/tracker-table";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/tracker")({
  component: HiringTracker,
});

const asAnswers = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** Every candidate who entered the funnel, answers included. Applicants and the people screening
 * turned away share one grid because "who did we turn away, and why" is the same question as "who
 * applied". */
function HiringTracker() {
  const { facilityId } = useFacility();
  const [filters, setFilters] = useState<TrackerFilterState>({
    jobId: ALL,
    type: ALL,
    status: ALL,
    board: ALL,
  });
  const [hidden, setHidden] = useState<string[]>([]);

  const jobId = filters.jobId === ALL ? undefined : filters.jobId;
  const jobs = useQuery(orpc.workforce.hiring.listJobs.queryOptions({ input: { facilityId } }));
  const applications = useEveryApplication({ facilityId, jobId });
  const screenedOut = useQuery(
    orpc.workforce.hiring.screenedOut.queryOptions({ input: { jobId } }),
  );

  const pending = applications.isPending || screenedOut.isPending || jobs.isPending;
  const failed = [applications, screenedOut, jobs].find((query) => query.isError);
  const boardByJob = new Map((jobs.data ?? []).map((job) => [job.id, job.boardName]));

  const applicantRows: TrackerRow[] = (applications.data?.rows ?? []).map((application) => ({
    key: `applicant-${application.id}`,
    type: "applicant",
    name: application.applicantName || "Unknown applicant",
    email: application.applicantEmail,
    phone: application.applicantPhone,
    jobId: application.jobId,
    jobTitle: application.jobTitle,
    boardName: boardByJob.get(application.jobId) ?? null,
    stage: application.currentStage,
    status: application.stageStatus,
    screeningPassed: application.screeningPassed,
    appliedAt: application.createdAt,
    applicationId: application.id,
    answers: asAnswers(application.screeningResponses),
    questions: normalizeScreeningQuestions(application.screeningQuestionsSnapshot),
  }));

  // `screenedOut` scopes by who is asking rather than by the facility switcher,
  // so the building's own jobs are what narrow it.
  const screenedRows: TrackerRow[] = (screenedOut.data ?? [])
    .filter((candidate) => facilityId === ALL || boardByJob.has(candidate.jobId))
    .map((candidate) => ({
      key: `screened-${candidate.id}`,
      type: "screened_out",
      name: `${candidate.firstName} ${candidate.lastName}`.trim(),
      email: candidate.email,
      phone: candidate.phone,
      jobId: candidate.jobId,
      jobTitle: candidate.jobTitle,
      boardName: boardByJob.get(candidate.jobId) ?? null,
      stage: null,
      status: null,
      screeningPassed: false,
      appliedAt: candidate.createdAt,
      applicationId: null,
      answers: asAnswers(candidate.screeningResponses),
      questions: normalizeScreeningQuestions(candidate.screeningQuestionsSnapshot),
    }));

  const allRows = [...applicantRows, ...screenedRows];
  // Every option comes from the rows in hand, so a filter never offers a value
  // that would return nothing.
  const statuses = [
    ...new Set(allRows.map((row) => row.status).filter((value): value is string => value !== null)),
  ].toSorted();
  const boards = [
    ...new Set(
      allRows.map((row) => row.boardName).filter((value): value is string => value !== null),
    ),
  ].toSorted();

  const filtered = Object.values(filters).some((value) => value !== ALL);
  const clearFilters = () => setFilters({ jobId: ALL, type: ALL, status: ALL, board: ALL });
  const narrowedRows = allRows.filter(
    (row) =>
      (filters.status === ALL || row.status === filters.status) &&
      (filters.board === ALL || row.boardName === filters.board),
  );
  const rows = narrowedRows.filter((row) => filters.type === ALL || row.type === filters.type);
  const typeCounts =
    pending || failed
      ? undefined
      : {
          [ALL]: narrowedRows.length,
          applicant: narrowedRows.filter((row) => row.type === "applicant").length,
          screened_out: narrowedRows.filter((row) => row.type === "screened_out").length,
        };

  const columns = trackerColumns(rows, jobId !== undefined);
  const visible = columns.filter((column) => !hidden.includes(column.id));
  const toggleColumn = (columnId: string) =>
    setHidden((current) =>
      current.includes(columnId) ? current.filter((id) => id !== columnId) : [...current, columnId],
    );

  /* Built by the server rather than from the rows on screen: the export is a
     file of candidates leaving the building, so it is audited, and it carries
     the screening answers that the table hides behind a column toggle. */
  const download = useMutation(
    orpc.workforce.hiring.trackerExport.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  return (
    <>
      <PageHeader
        title="Tracker"
        count={pending || failed ? undefined : rows.length}
        meta={
          pending || failed
            ? undefined
            : jobId
              ? "Everyone who entered the funnel for this job, with their screening answers"
              : "Choose one job to see its screening answers as columns"
        }
        actions={
          <Button
            variant="outline"
            disabled={rows.length === 0 || download.isPending}
            onClick={() =>
              download.mutate({
                facilityId,
                ...(jobId ? { jobId } : {}),
                ...(filters.type === ALL
                  ? {}
                  : { type: filters.type as "applicant" | "screened_out" }),
              })
            }
          >
            <Download className="size-4" />
            {download.isPending ? "Preparing…" : "Download CSV"}
          </Button>
        }
      />

      <TrackerFilters
        filters={filters}
        onChange={setFilters}
        typeCounts={typeCounts}
        jobs={jobs.data ?? []}
        statuses={statuses}
        boards={boards}
        columns={columns}
        isVisible={(columnId) => !hidden.includes(columnId)}
        onToggleColumn={toggleColumn}
      />

      {pending ? (
        <Skeleton className="h-64 w-full" />
      ) : failed ? (
        <ReadFailed
          message="Could not load the tracker."
          error={failed.error}
          retrying={failed.isFetching}
          onRetry={() => void failed.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={filtered ? "No candidates match these filters" : "Nobody has applied yet"}
          description={
            filtered
              ? "Widen the filters to see more of the funnel."
              : "Everyone who applies appears here, including the people screening turned away."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TrackerTable rows={rows} columns={visible} />
      )}
    </>
  );
}
